<template>
  <div class="sx">
    <header class="sx-head">
      <span class="sx-eyebrow">Paramètres</span>
      <h1 class="sx-title">Banques</h1>
      <p class="sx-sub">Banques proposées pour les RIB des clients et des utilisateurs (virements, paiements).</p>
    </header>

    <SettingsNav />

    <div class="sx-grid">
      <!-- ── Add ─────────────────────────────────────────────────── -->
      <form class="sx-card sx-form-card" novalidate @submit.prevent="submitForm">
        <header class="sx-card-head">
          <span class="sx-card-icon"><i class="ph ph-bank" aria-hidden="true"></i></span>
          <div>
            <h2>Nouvelle banque</h2>
            <p>Elle sera proposée dans les formulaires de RIB.</p>
          </div>
        </header>

        <div class="sx-field">
          <label for="bk-new-name">Nom de la banque <span class="sx-req" aria-hidden="true">*</span></label>
          <input
            id="bk-new-name"
            v-model="bank.name"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.name }"
            maxlength="100"
            placeholder="Ex. Attijariwafa Bank"
            @input="delete errors.name"
          />
          <span v-if="errors.name" class="sx-err">{{ errors.name }}</span>
        </div>
        <div class="sx-field">
          <label for="bk-new-code">Code de la banque</label>
          <input
            id="bk-new-code"
            v-model="bank.code"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.code }"
            maxlength="20"
            placeholder="Ex. 007"
            @input="delete errors.code"
          />
          <span v-if="errors.code" class="sx-err">{{ errors.code }}</span>
          <span v-else class="sx-help">Code bancaire (3 premiers chiffres du RIB), facultatif.</span>
        </div>

        <button type="submit" class="btn btn-primary sx-submit" :disabled="saving">
          <span v-if="saving" class="spinner-border spinner-border-sm" role="status"></span>
          Ajouter la banque
        </button>
      </form>

      <!-- ── List ────────────────────────────────────────────────── -->
      <section class="sx-card" aria-labelledby="bk-list-title">
        <div class="sx-list-head">
          <h2 id="bk-list-title">Banques <span class="sx-count">{{ banks.length }}</span></h2>
          <div class="sx-search">
            <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
            <input v-model="search" type="search" placeholder="Rechercher une banque…" aria-label="Rechercher une banque" />
          </div>
        </div>

        <div v-if="loading">
          <span v-for="n in 4" :key="n" class="sx-skel"></span>
        </div>
        <div v-else-if="loadError" class="sx-load-error" role="alert">
          <i class="ph ph-warning-circle" aria-hidden="true"></i>
          Impossible de charger les banques.
          <button type="button" class="btn btn-sm btn-light" @click="fetchBanks">Réessayer</button>
        </div>
        <div v-else-if="!filtered.length" class="sx-empty">
          <i class="ph ph-bank" aria-hidden="true"></i>
          <p>{{ search ? "Aucune banque ne correspond à votre recherche." : "Aucune banque pour le moment." }}</p>
        </div>
        <ul v-else class="sx-rows">
          <li v-for="item in filtered" :key="item.id" class="sx-row" :class="{ 'is-new': item.id === lastAddedId }">
            <span class="sx-avatar" aria-hidden="true">{{ initials(item.name) }}</span>
            <div class="sx-main">
              <strong>{{ item.name }}</strong>
              <small><span v-if="item.code" class="sx-code">{{ item.code }}</span><span v-else>Sans code</span></small>
            </div>
            <div class="sx-meta">
              <span class="sx-tag"><i class="ph ph-user" aria-hidden="true"></i><b>{{ num(item.NbUsers) }}</b> utilisateur(s)</span>
              <span class="sx-tag"><i class="ph ph-storefront" aria-hidden="true"></i><b>{{ num(item.NbClient) }}</b> client(s)</span>
            </div>
            <div class="sx-actions">
              <button type="button" class="sx-icon-btn" :aria-label="`Modifier ${item.name}`" title="Modifier" @click="openEditModal(item.id)">
                <i class="ph ph-pencil-simple" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                class="sx-icon-btn is-danger"
                :aria-label="`Supprimer ${item.name}`"
                :title="num(item.NbClient) > 0 ? 'Impossible : banque utilisée par des clients' : 'Supprimer'"
                :disabled="num(item.NbClient) > 0"
                @click="deleteType(item.id)"
              >
                <i class="ph ph-trash" aria-hidden="true"></i>
              </button>
            </div>
          </li>
        </ul>
      </section>
    </div>

    <!-- ── Edit modal ────────────────────────────────────────────── -->
    <div id="editBankModal" ref="editModal" class="modal fade" tabindex="-1" aria-labelledby="editBankModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <form class="modal-content" novalidate @submit.prevent="updateBank">
          <div class="modal-header">
            <h2 id="editBankModalLabel" class="modal-title">Modifier la banque</h2>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fermer"></button>
          </div>
          <div class="modal-body">
            <div class="sx-field">
              <label for="bk-edit-name">Nom de la banque <span class="sx-req" aria-hidden="true">*</span></label>
              <input id="bk-edit-name" v-model="editBank.name" type="text" class="form-control" :class="{ 'is-invalid': editErrors.name }" maxlength="100" />
              <span v-if="editErrors.name" class="sx-err">{{ editErrors.name }}</span>
            </div>
            <div class="sx-field">
              <label for="bk-edit-code">Code de la banque</label>
              <input id="bk-edit-code" v-model="editBank.code" type="text" class="form-control" maxlength="20" />
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Annuler</button>
            <button type="submit" class="btn btn-primary" :disabled="savingEdit">
              <span v-if="savingEdit" class="spinner-border spinner-border-sm" role="status"></span>
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import SettingsNav from "./SettingsNav.vue";
import { computed, defineComponent, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import api from "@/utils/axios";
import { useAppToast } from "@/composables/useAppToast";
import Swal from "sweetalert2";
import { Modal } from "bootstrap";

interface Bank {
  id: number;
  name: string;
  code: string | null;
  NbUsers?: number;
  NbClient?: number;
}

const norm = (s: string) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

export default defineComponent({
  name: "BankSettings",
  components: { SettingsNav },
  setup() {
    const { showToast } = useAppToast();
    const banks = ref<Bank[]>([]);
    const loading = ref(true);
    const loadError = ref(false);
    const search = ref("");
    const lastAddedId = ref<number | null>(null);

    const num = (v: unknown) => Number(v) || 0;
    const initials = (name: string) =>
      (name || "?")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p[0] || "")
        .join("")
        .toUpperCase();

    async function fetchBanks() {
      loading.value = true;
      loadError.value = false;
      try {
        const response = await api.get("/api/auth/bank_list");
        banks.value = response.data || [];
      } catch (error) {
        console.error("Error fetching banks:", error);
        loadError.value = true;
      } finally {
        loading.value = false;
      }
    }
    onMounted(fetchBanks);

    const filtered = computed(() => {
      const q = norm(search.value.trim());
      return q ? banks.value.filter((b) => norm(b.name).includes(q) || norm(b.code || "").includes(q)) : banks.value;
    });

    // ── Create ──
    const bank = reactive({ name: "", code: "" });
    const errors = reactive<Record<string, string>>({});
    const saving = ref(false);

    async function submitForm() {
      Object.keys(errors).forEach((k) => delete errors[k]);
      const name = bank.name.trim();
      if (!name) errors.name = "Le nom de la banque est obligatoire.";
      else if (banks.value.some((b) => norm(b.name) === norm(name))) errors.name = "Cette banque existe déjà.";
      if (Object.keys(errors).length || saving.value) return;

      saving.value = true;
      try {
        const formData = new FormData();
        formData.append("name", name);
        formData.append("code", bank.code.trim());
        const response = await api.post("/api/auth/bank_add", formData);
        if (response.data?.success || response.data?.message === "success") {
          lastAddedId.value = response.data?.data?.id ?? null;
          await fetchBanks();
          bank.name = "";
          bank.code = "";
          showToast("Banque ajoutée.", "success");
        } else {
          showToast(response.data?.message || "L'ajout a échoué.", "error");
        }
      } catch (error: any) {
        const errs = error?.response?.data?.errors || {};
        Object.keys(errs).forEach((k) => (errors[k] = errs[k][0]));
        showToast(error?.response?.data?.message || "Une erreur est survenue lors de l'ajout.", "error");
      } finally {
        saving.value = false;
      }
    }

    // ── Delete ──
    function deleteType(id: number) {
      const target = banks.value.find((b) => b.id === id);
      Swal.fire({
        title: "Supprimer cette banque ?",
        text: target ? `« ${target.name} » sera définitivement supprimée.` : "",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#E44A4A",
        confirmButtonText: "Oui, supprimer",
        cancelButtonText: "Annuler",
        reverseButtons: true,
      }).then(async (result: any) => {
        if (!result.isConfirmed) return;
        try {
          const res = await api.post(`/api/auth/removeBank/${id}`);
          if (res.data?.success) {
            showToast("Banque supprimée.", "success");
            fetchBanks();
          } else {
            showToast(res.data?.message ?? "Vous ne pouvez pas supprimer cette banque.", "error");
          }
        } catch (error: any) {
          showToast(error?.response?.data?.message || "Erreur lors de la suppression de la banque.", "error");
        }
      });
    }

    // ── Edit ──
    const editBank = ref<{ id: number | null; name: string; code: string }>({ id: null, name: "", code: "" });
    const editErrors = reactive<Record<string, string>>({});
    const editModal = ref<HTMLElement | null>(null);
    const savingEdit = ref(false);
    let modalInstance: Modal | null = null;

    const openEditModal = (id: number) => {
      const found = banks.value.find((b) => b.id === id);
      if (!found) return;
      editBank.value = { id: found.id, name: found.name, code: found.code || "" };
      Object.keys(editErrors).forEach((k) => delete editErrors[k]);
      if (!modalInstance && editModal.value) modalInstance = new Modal(editModal.value);
      modalInstance?.show();
    };

    const updateBank = async () => {
      Object.keys(editErrors).forEach((k) => delete editErrors[k]);
      const name = editBank.value.name.trim();
      if (!name) editErrors.name = "Le nom de la banque est obligatoire.";
      else if (banks.value.some((b) => b.id !== editBank.value.id && norm(b.name) === norm(name)))
        editErrors.name = "Cette banque existe déjà.";
      if (Object.keys(editErrors).length || savingEdit.value) return;

      savingEdit.value = true;
      try {
        // only the editable fields (the row also carries NbUsers / NbClient)
        const res = await api.post(`/api/auth/bank_edit/${editBank.value.id}`, {
          name,
          code: editBank.value.code.trim(),
        });
        if (res.data?.success) {
          modalInstance?.hide();
          await fetchBanks();
          showToast("Banque modifiée.", "success");
        } else {
          showToast(res.data?.message ?? "Erreur lors de la mise à jour.", "error");
        }
      } catch (error: any) {
        const errs = error?.response?.data?.errors || {};
        Object.keys(errs).forEach((k) => (editErrors[k] = errs[k][0]));
        showToast(error?.response?.data?.message || "Erreur lors de la mise à jour.", "error");
      } finally {
        savingEdit.value = false;
      }
    };
    onBeforeUnmount(() => modalInstance?.dispose());

    return {
      banks,
      loading,
      loadError,
      search,
      filtered,
      lastAddedId,
      num,
      initials,
      fetchBanks,
      bank,
      errors,
      saving,
      submitForm,
      deleteType,
      editBank,
      editErrors,
      editModal,
      savingEdit,
      openEditModal,
      updateBank,
    };
  },
});
</script>
